import GlassCard from '@/components/GlassCard';
import PageTitle from '@/components/PageTitle';
import ThemeWrapper from '@/components/ThemeWrapper';
import AppLayout from '@/layouts/AppLayout';
import { Flashcard, FlashcardSetSummary } from '@/types';
import { cleanWordPressHtml } from '@/utils/cleanHtml';
import { Link, usePage } from '@inertiajs/react';
import clsx from 'clsx';
import {
    ArrowLeft,
    ChevronLeft,
    ChevronRight,
    RotateCcw,
    Shuffle,
    Star,
} from 'lucide-react';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface SetInfo {
    id: number;
    title: string;
    slug: string;
    level: string;
    theme: string;
}

interface Props {
    set?: SetInfo;
    cards?: Flashcard[];
    sets_in_level?: FlashcardSetSummary[];
}

const REVIEW_KEY = 'flashcard-review';

const readReviewMarks = (): Set<number> => {
    try {
        const raw = window.localStorage.getItem(REVIEW_KEY);
        return new Set<number>(raw ? JSON.parse(raw) : []);
    } catch {
        return new Set<number>();
    }
};

const StudyContent: React.FC = () => {
    const { t } = useTranslation();
    const { props } = usePage<Props>();
    const { set, cards = [], sets_in_level = [] } = props;

    const [order, setOrder] = useState<number[]>(() => cards.map((_, index) => index));
    const [position, setPosition] = useState(0);
    const [flipped, setFlipped] = useState(false);
    const [reviewMarks, setReviewMarks] = useState<Set<number>>(() => readReviewMarks());

    const currentCard = useMemo(
        () => cards[order[position]],
        [cards, order, position],
    );

    const goTo = (nextPosition: number) => {
        setFlipped(false);
        setPosition(nextPosition);
    };

    const handlePrev = () => goTo((position - 1 + order.length) % order.length);
    const handleNext = () => goTo((position + 1) % order.length);

    const handleShuffle = () => {
        // Fisher–Yates on a copy of the current order.
        const shuffled = [...order];
        for (let i = shuffled.length - 1; i > 0; i -= 1) {
            const j = Math.floor(Math.random() * (i + 1));
            [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
        }
        setOrder(shuffled);
        goTo(0);
    };

    const handleReset = () => {
        setOrder(cards.map((_, index) => index));
        goTo(0);
    };

    const toggleReview = () => {
        if (!currentCard) return;
        const next = new Set(reviewMarks);
        if (next.has(currentCard.id)) {
            next.delete(currentCard.id);
        } else {
            next.add(currentCard.id);
        }
        setReviewMarks(next);
        try {
            window.localStorage.setItem(REVIEW_KEY, JSON.stringify([...next]));
        } catch {
            // localStorage unavailable — review marks are best-effort only.
        }
    };

    if (!set) return null;

    const isMarked = currentCard ? reviewMarks.has(currentCard.id) : false;
    const otherSets = sets_in_level.filter((sibling) => sibling.slug !== set.slug);

    return (
        <AppLayout>
            <PageTitle title={set.title} />
            <div className="mx-auto max-w-3xl space-y-6">
                <Link
                    href="/flashcards/"
                    className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 dark:text-neutral-400 dark:hover:text-neutral-200"
                >
                    <ArrowLeft size={16} /> {t('flashcards.backToSets')}
                </Link>

                <h1 className="text-center text-2xl font-bold text-gray-800 dark:text-white">
                    {set.title}
                </h1>

                {cards.length === 0 ? (
                    <GlassCard className="p-10 text-center text-gray-600 dark:text-neutral-300">
                        {t('flashcards.noSets')}
                    </GlassCard>
                ) : (
                    <>
                        {/* Flip card */}
                        <div className="[perspective:1600px]">
                            <button
                                type="button"
                                onClick={() => setFlipped((value) => !value)}
                                className="relative block min-h-[320px] w-full text-left [transform-style:preserve-3d] transition-transform duration-500"
                                style={{
                                    transform: flipped ? 'rotateY(180deg)' : 'rotateY(0deg)',
                                }}
                                aria-label={t('flashcards.flipHint')}
                            >
                                {/* Front */}
                                <span
                                    className="absolute inset-0 flex flex-col overflow-hidden rounded-2xl border border-white/50 bg-white p-8 shadow-lg [backface-visibility:hidden] dark:border-white/10 dark:bg-neutral-800"
                                >
                                    <span
                                        className="flex flex-1 flex-col items-center justify-center text-center text-lg text-gray-800 [&_audio]:mt-3 [&_audio]:w-full [&_img]:mx-auto [&_img]:h-auto [&_img]:max-w-full dark:text-neutral-100"
                                        dangerouslySetInnerHTML={{
                                            __html: cleanWordPressHtml(currentCard?.front),
                                        }}
                                    />
                                    <span className="mt-4 h-2 rounded-full bg-gradient-to-r from-theme-primary to-theme-secondary" />
                                </span>
                                {/* Back */}
                                <span
                                    className="absolute inset-0 flex flex-col overflow-hidden rounded-2xl border border-white/50 bg-theme-light p-8 shadow-lg [backface-visibility:hidden] [transform:rotateY(180deg)] dark:border-white/10 dark:bg-neutral-700"
                                >
                                    <span
                                        className="flex flex-1 flex-col items-center justify-center text-center text-lg font-medium text-gray-800 [&_audio]:mt-3 [&_audio]:w-full [&_img]:mx-auto [&_img]:h-auto [&_img]:max-w-full dark:text-neutral-100"
                                        dangerouslySetInnerHTML={{
                                            __html: cleanWordPressHtml(currentCard?.back),
                                        }}
                                    />
                                    <span className="mt-4 h-2 rounded-full bg-gradient-to-r from-theme-primary to-theme-secondary" />
                                </span>
                            </button>
                        </div>

                        {/* Controls */}
                        <div className="flex items-center justify-between">
                            <button
                                type="button"
                                onClick={handlePrev}
                                className="rounded-full bg-white/70 p-2 text-theme-primary-text shadow hover:bg-white dark:bg-white/10"
                                aria-label={t('flashcards.previous')}
                            >
                                <ChevronLeft />
                            </button>

                            <div className="flex items-center gap-4">
                                <span className="text-sm font-medium text-gray-600 dark:text-neutral-300">
                                    {position + 1}/{order.length}
                                </span>
                                <button
                                    type="button"
                                    onClick={handleShuffle}
                                    className="rounded-full bg-white/70 p-2 text-theme-primary-text shadow hover:bg-white dark:bg-white/10"
                                    aria-label={t('flashcards.shuffle')}
                                    title={t('flashcards.shuffle')}
                                >
                                    <Shuffle size={18} />
                                </button>
                                <button
                                    type="button"
                                    onClick={handleReset}
                                    className="rounded-full bg-white/70 p-2 text-theme-primary-text shadow hover:bg-white dark:bg-white/10"
                                    aria-label={t('flashcards.reset')}
                                    title={t('flashcards.reset')}
                                >
                                    <RotateCcw size={18} />
                                </button>
                            </div>

                            <button
                                type="button"
                                onClick={handleNext}
                                className="rounded-full bg-white/70 p-2 text-theme-primary-text shadow hover:bg-white dark:bg-white/10"
                                aria-label={t('flashcards.next')}
                            >
                                <ChevronRight />
                            </button>
                        </div>

                        {/* Review-later toggle */}
                        <button
                            type="button"
                            onClick={toggleReview}
                            className={clsx(
                                'mx-auto flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition-colors',
                                isMarked
                                    ? 'bg-theme-primary/15 text-theme-primary-text dark:text-neutral-100'
                                    : 'bg-white/70 text-gray-600 dark:bg-white/10 dark:text-neutral-300',
                            )}
                        >
                            <Star
                                size={16}
                                className={isMarked ? 'fill-current text-theme-primary-text' : ''}
                            />
                            {t('flashcards.markReview')}
                        </button>
                    </>
                )}

                {/* Other sets in this level */}
                {otherSets.length > 0 ? (
                    <div className="space-y-2 pt-4">
                        <h2 className="text-sm font-semibold text-gray-500 dark:text-neutral-400">
                            {t('flashcards.moreInLevel', { level: set.level })}
                        </h2>
                        {otherSets.map((sibling) => (
                            <Link
                                key={sibling.id}
                                href={`/flashcards/${sibling.slug}/`}
                                className="block rounded-full border border-theme-border bg-theme-primary/10 px-5 py-3 text-sm font-semibold text-theme-primary-text transition-colors hover:bg-theme-primary/20 dark:text-neutral-100"
                            >
                                {sibling.title}
                            </Link>
                        ))}
                    </div>
                ) : null}
            </div>
        </AppLayout>
    );
};

const Study: React.FC<Props> = () => {
    return (
        <ThemeWrapper>
            <StudyContent />
        </ThemeWrapper>
    );
};

export default Study;
